import { datadogRum } from "@datadog/browser-rum";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { watchPopupPositions } from "@utils/popupPositionWatch";
import { reportScrollGlobalWrites } from "@utils/scrollGlobalGuard";
import { APP_ENV, ENV_NAMES, cookieDomain, tracingUrl } from "@utils/utils";
import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FronteraAuthProvider } from "@fronterahealth/frontera-auth0-react";
import { TooltipProvider } from "@fronterahealth/frontera-ui-components/ui/tooltip";

import "@components/notifications/notifications.css";
import { AdminDataProvider } from "@providers/AdminDataProvider";
import { AnalyticsProvider } from "@providers/AnalyticsProvider";
import { FeatureFlagProvider } from "@providers/FeatureFlagProvider";
import { InternationalizationProvider } from "@providers/InternationalizationProvider";
import { NotificationsProvider } from "@providers/NotificationsProvider";

import "./index.css";
import "./polyfills";
import { isPublicPath, publicRouter } from "./public-router";
import { router } from "./router";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

datadogRum.init({
  applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
  clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
  site: "us3.datadoghq.com",
  service: "frontera-portal-ui",
  env: APP_ENV,
  version: import.meta.env.VITE_COMMIT_HASH || "<missing-version>",
  sessionSampleRate: 100,
  sessionReplaySampleRate: APP_ENV === ENV_NAMES.PROD ? 100 : 0,
  allowedTracingUrls: tracingUrl ? [tracingUrl] : undefined,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: "mask-user-input",
});
// PROA-2811/PROA-3108 diagnostics, temporary; PROA-3108's follow-ups set when each comes out. The guard
// (index.html <script>, public/scroll-global-guard.js, scrollGlobalGuard.ts) has its own exit; the
// watcher goes with the root-cause fix.
reportScrollGlobalWrites();
watchPopupPositions();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {isPublicPath(window.location.pathname) ? (
      <QueryClientProvider client={queryClient}>
        <ToastContainer pauseOnFocusLoss={false} autoClose={2000} hideProgressBar />
        <RouterProvider router={publicRouter} />
      </QueryClientProvider>
    ) : (
      <FronteraAuthProvider
        loginOnMount={true}
        domain={import.meta.env.VITE_AUTH0_DOMAIN}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        audience={import.meta.env.VITE_AUTH0_AUDIENCE}
        cookieDomain={cookieDomain}
        onRedirectCallback={(appState) =>
          router.navigate(appState?.returnTo || window.location.pathname, { replace: true })
        }
      >
        <InternationalizationProvider>
          <QueryClientProvider client={queryClient}>
            <AdminDataProvider>
              <NotificationsProvider>
                <ToastContainer pauseOnFocusLoss={false} autoClose={2000} hideProgressBar />
                <FeatureFlagProvider>
                  <AnalyticsProvider>
                    <TooltipProvider>
                      <RouterProvider router={router} />
                    </TooltipProvider>
                  </AnalyticsProvider>
                </FeatureFlagProvider>
              </NotificationsProvider>
            </AdminDataProvider>
          </QueryClientProvider>
        </InternationalizationProvider>
      </FronteraAuthProvider>
    )}
  </React.StrictMode>,
);
